import type {
  CartLine,
  PaymentInput,
  PaymentSummary,
  Transaction,
  TransactionDraft,
} from '@/domain/entities/transaction.entity'
import { calculateLine } from '@/domain/pricing/calculate-totals'
import type { PaymentSummaryDto, TransactionDto } from './transaction.dto'

const TRANSACTION_STATUSES = ['open', 'success', 'cancel', 'invalid'] as const

function toStatus(raw: string | null | undefined): Transaction['status'] {
  return (TRANSACTION_STATUSES as readonly string[]).includes(raw ?? '')
    ? (raw as Transaction['status'])
    : 'open'
}

function toPaymentSummary(dto: PaymentSummaryDto | null | undefined): PaymentSummary | null {
  if (!dto) return null
  return {
    grandTotal: dto.grand_total ?? 0,
    paid: dto.paid ?? 0,
    outstanding: dto.outstanding ?? 0,
    change: dto.change ?? 0,
  }
}

function toItemBody(line: CartLine) {
  let itemDiscAmount = 0
  if (line.discount) {
    const addonPerUnit = line.addons.reduce((sum, a) => sum + a.price * a.qty, 0)
    const gross = (line.unitPrice + addonPerUnit) * line.qty
    if (line.discount.type === 'percent') {
      itemDiscAmount = Math.min(
        Math.floor((gross * Math.min(line.discount.value, 100)) / 100),
        gross,
      )
    } else if (line.discount.type === 'amount') {
      itemDiscAmount = Math.min(line.discount.value, gross)
    }
  }

  const amounts = calculateLine({
    unitPrice: line.unitPrice,
    qty: line.qty,
    addons: line.addons,
    discountAmount: itemDiscAmount,
    status: 'normal',
  })

  return {
    client_line_id: line.key,
    product_id: line.productId,
    source_id: line.sourceId || null,
    sku: line.sku ?? '',
    name: line.name,
    qty: line.qty,
    // Sudah termasuk harga variant — `variant.price` di bawah hanya menyimpan
    // asal-usulnya, bukan angka yang dijumlahkan lagi.
    unit_price: line.unitPrice,
    manual_hpp: line.manualHpp ?? null,
    variant: line.variant
      ? {
          variant_id: line.variant.id,
          name: line.variant.name,
          price: line.variant.price,
          source_id: line.variant.sourceId || null,
          manual_hpp: line.variant.manualHpp ?? null,
        }
      : null,
    addons: line.addons.map((a) => ({
      addon_id: a.id,
      name: a.name,
      price: a.price,
      qty: a.qty,
      source_id: a.sourceId || null,
      manual_hpp: a.manualHpp ?? null,
    })),
    discount: line.discount
      ? {
          discount_id: line.discount.discountId || null,
          name: line.discount.name || 'Diskon Item',
          type: line.discount.type,
          value: line.discount.value,
          amount: amounts.discountAmount,
          coupon_code: line.discount.couponCode || null,
          reason: line.discount.reason || '',
        }
      : null,
    line_status: 'normal',
    notes: line.notes,
    // Kosong = server memilih sendiri: "queued" bila outlet berdapur, "none"
    // bila tidak. Layar kasir tidak berhak memutuskan itu.
    kitchen_status: '',
    gross: amounts.gross,
    discount_amount: amounts.discountAmount,
    line_total: amounts.lineTotal,
    is_package: line.isPackage,
    components: line.components.map((c) => {
      const compQty = c.qty * line.qty
      const compAmounts = calculateLine({
        unitPrice: 0,
        qty: line.qty,
        addons: c.addons,
        discountAmount: 0,
        status: 'normal',
      })

      return {
        product_id: c.productId,
        source_id: c.sourceId || null,
        sku: c.sku,
        name: c.name,
        qty: compQty,
        unit_price: 0,
        line_total: compAmounts.lineTotal,
        variant: c.variant
          ? {
              variant_id: c.variant.id,
              name: c.variant.name,
              price: c.variant.price,
              source_id: c.variant.sourceId || null,
            }
          : null,
        addons: c.addons.map((a) => ({
          addon_id: a.id,
          name: a.name,
          price: a.price,
          qty: a.qty,
          source_id: a.sourceId || null,
        })),
        kitchen_status: 'queued',
      }
    }),
  }
}

/**
 * `tax`, `service`, `rounding`, dan `totals` adalah objek biasa, bukan pointer:
 * tidak mengirimnya sama dengan mengirim nol, dan transaksinya akan tersimpan
 * dengan grand total 0. Keempatnya SELALU dikirim.
 */
function toCreateBody(outletId: string, draft: TransactionDraft) {
  const { outlet, totals } = draft

  return {
    client_id: draft.clientId,
    outlet_id: outletId,
    invoice_no: '',
    source: 'online',
    occurred_at: draft.occurredAt,
    status: draft.payment ? 'success' : 'open',
    price_tier: draft.priceTier,
    notes: draft.notes,
    discount: draft.discount
      ? {
          discount_id: draft.discount.discountId || null,
          name: draft.discount.name || 'Diskon Struk',
          type: draft.discount.type,
          value: draft.discount.value,
          amount: totals.discountAmount,
          coupon_code: draft.discount.couponCode || null,
          reason: draft.discount.reason || '',
        }
      : null,
    tax: {
      type:
        outlet.taxType ??
        (outlet.taxInclusive ? 'inclusive_after_discount' : 'exclusive_after_discount'),
      inclusive: outlet.taxInclusive,
      value: outlet.taxValue,
      amount: totals.taxAmount,
    },
    service: {
      mode: outlet.serviceChargeMode ?? 'service_charge_is_taxed',
      inclusive: outlet.taxInclusive,
      value: outlet.serviceValue,
      amount: totals.serviceAmount,
    },
    rounding: {
      enabled: outlet.roundingEnabled,
      unit: outlet.roundingUnit,
      mode: outlet.roundingMode,
      amount: totals.roundingAmount,
    },
    totals: {
      subtotal: totals.subtotal,
      discount_amount: totals.discountAmount,
      after_discount: totals.afterDiscount,
      compliment_value: totals.complimentValue,
      grand_total: totals.grandTotal,
    },
    items: draft.lines.map(toItemBody),
    payments: draft.payment ? [toPaymentBody(draft.payment)] : [],
  }
}

function toPaymentBody(input: PaymentInput) {
  return {
    payment_method_id: input.paymentMethodId,
    amount: input.amount,
    // Dikirim hanya bila ada uang fisik. `null` dan `0` berbeda arti, jadi
    // field-nya dihilangkan alih-alih dikirim 0.
    ...(input.tendered !== null ? { tendered: input.tendered } : {}),
    ...(input.referenceNo ? { reference_no: input.referenceNo } : {}),
  }
}

export const transactionMapper = {
  toTransaction(dto: TransactionDto): Transaction {
    return {
      id: dto.id,
      invoiceNo: dto.invoice_no ?? '',
      status: toStatus(dto.status),
      grandTotal: dto.totals?.grand_total ?? 0,
      paymentSummary: toPaymentSummary(dto.payment_summary),
    }
  },

  toCreateBody,
  toPaymentBody,
}
